import React, { useState, useEffect, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { axiosInstance } from "@/App";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ensureCsvPassword } from "@/lib/csvPassword";
import { Plus, UserCheck, Trash2, Upload, Download, Edit, FileText, Eye, EyeOff, RefreshCw, Sparkles, FileImage, IdCard, Building2 } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { getCemigRule, cemigCommission, clampCemigDiscount } from "@/lib/cemigRules";

export default function Cooperatives({ user, onLogout }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [cooperatives, setCooperatives] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importErrors, setImportErrors] = useState([]);
  const [importId, setImportId] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedCoopId, setSelectedCoopId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCoops, setSelectedCoops] = useState([]);
  const [filterDistributor, setFilterDistributor] = useState("");
  const [filterPowerPlant, setFilterPowerPlant] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterPartner, setFilterPartner] = useState("");
  const [filterModality, setFilterModality] = useState("");
  const [filterGroup, setFilterGroup] = useState("");
  const [powerPlants, setPowerPlants] = useState([]);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [serverFilters, setServerFilters] = useState({ distributors: [], modalities: [] });
  const fetchSeq = useRef(0);
  // Google Drive removed - using local storage
  const [uploading, setUploading] = useState(false);
  const [editingCoop, setEditingCoop] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [documents, setDocuments] = useState({
    rg_cpf_file: null,
    cnpj_card_file: null
  });
  
  // Helper function to check if user can edit
  const canEdit = () => {
    return user?.role === 'admin' || user?.role === 'operador';
  };
  const fileInputRef = useRef(null);
  const ucFileInputRef = useRef(null);
  const rgCpfFileRef = useRef(null);
  const energyBillFileRef = useRef(null);
  const newRgCpfFileRef = useRef(null);
  const newEnergyBillFileRef = useRef(null);
  const newCnpjCardFileRef = useRef(null);
  
  // Mapeamento de comissões e descontos por distribuidora
  // Configurações dinâmicas carregadas do backend
  const [distributorCommissions, setDistributorCommissions] = useState({});
  const [distributorDiscounts, setDistributorDiscounts] = useState({});
  const [autoconsumoDiscounts, setAutoconsumoDiscounts] = useState({});
  
  // Estrutura para múltiplas instalações
  const [installations, setInstallations] = useState([
    {
      installation_number: "",
      installation_class: "",
      street: "",
      number: "",
      complement: "",
      zip_code: "",
      state: "",
      city: "",
      neighborhood: "",
      installation_notes: "",
      energy_bill_file_ref: React.createRef()
    }
  ]);

  // Funções para gerenciar instalações
  const addInstallation = () => {
    setInstallations([...installations, {
      installation_number: "",
      installation_class: "",
      street: "",
      number: "",
      complement: "",
      zip_code: "",
      state: "",
      city: "",
      neighborhood: "",
      installation_notes: "",
      energy_bill_file_ref: React.createRef()
    }]);
  };

  const removeInstallation = (index) => {
    if (installations.length > 1) {
      const newInstallations = installations.filter((_, i) => i !== index);
      setInstallations(newInstallations);
    }
  };

  const updateInstallation = (index, field, value) => {
    const newInstallations = [...installations];
    newInstallations[index][field] = value;
    setInstallations(newInstallations);
  };

  const [formData, setFormData] = useState({
    registration_type: "PF",
    name: "",
    email: "",
    phone: "",
    cpf_cnpj: "",
    birth_date: "",
    legal_representative_cpf: "",
    legal_representative_name: "",
    compensation_modality: "Geração Compartilhada",
    distributor: "",  // Vazio para forçar seleção
    distributor_login: "",
    distributor_password: "",
    generator_installation_number: "",
    agent_number: "",
    partner_id: "",
    partner_commission: "",
    signature_date: "",
    status_date: "",
    tariff_discount: "",
    contracted_kwh: "",
    rg_cpf_document: "",
    energy_bill_document: "",
    cnpj_card_document: "",  // Para PJ
    origin: "APP",  // Preenchido automaticamente
    situation: "pending"  // Default: Pendente
  });

  // ─── IA: preenchimento automático por upload ───
  const [aiExtracting, setAiExtracting] = useState(null); // 'invoice' | 'id' | 'cnpj' | null
  const [aiFilledFields, setAiFilledFields] = useState([]);
  const aiInvoiceRef = useRef(null);
  const aiIdDocRef = useRef(null);
  const aiCnpjRef = useRef(null);

  const _validateAiFile = (file) => {
    const ok = ["application/pdf", "image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!ok.includes(file.type)) {
      toast.error("Envie PDF, JPG, PNG ou WebP.");
      return false;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast.error("Arquivo excede 15 MB.");
      return false;
    }
    return true;
  };

  const _applyAiPatch = (patch, sourceLabel) => {
    const filled = Object.keys(patch);
    if (filled.length === 0) {
      toast.warning("A IA não identificou nenhum campo — tente uma imagem mais nítida.");
      return;
    }
    setFormData((prev) => ({ ...prev, ...patch }));
    setAiFilledFields(filled);
    toast.success(`IA (${sourceLabel}) preencheu ${filled.length} campo(s). Revise antes de salvar.`);
  };

  const handleAIExtractInvoice = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !_validateAiFile(file)) { e.target.value = ""; return; }
    setAiExtracting("invoice");
    const toastId = toast.loading("Lendo fatura com IA…");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const resp = await axiosInstance.post("/cooperatives/ai/extract-invoice", fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 90000,
      });
      const ex = resp?.data?.extracted || {};
      const patch = {};
      const filled = [];
      if (ex.cooperative_name) { patch.name = ex.cooperative_name; filled.push("name"); }
      if (ex.cpf_cnpj) { patch.cpf_cnpj = ex.cpf_cnpj; filled.push("cpf_cnpj"); }
      if (ex.email) { patch.email = ex.email; filled.push("email"); }
      if (ex.phone) { patch.phone = ex.phone; filled.push("phone"); }
      if (ex.birth_date && formData.registration_type === "PF") { patch.birth_date = ex.birth_date; filled.push("birth_date"); }
      if (ex.distributor) { patch.distributor = String(ex.distributor).trim().toUpperCase(); filled.push("distributor"); }

      const hist = Array.isArray(ex.consumption_history) ? ex.consumption_history.filter((n) => typeof n === "number" && n > 0) : [];
      let annualAvg = null;
      if (hist.length > 0) annualAvg = Math.round(hist.reduce((a, b) => a + b, 0) / hist.length);
      else if (typeof ex.consumption === "number") annualAvg = Math.round(ex.consumption);
      if (annualAvg) { patch.contracted_kwh = String(annualAvg); filled.push("contracted_kwh"); }

      setFormData((prev) => ({ ...prev, ...patch }));

      if (ex.installation_number && typeof updateInstallation === "function") {
        updateInstallation(0, "installation_number", String(ex.installation_number));
        filled.push("installation_number");
      }
      if (annualAvg && typeof updateInstallation === "function") {
        updateInstallation(0, "contracted_kwh", String(annualAvg));
      }

      setAiFilledFields(filled);
      const note = hist.length > 0 ? ` (média de ${hist.length} meses = ${annualAvg} kWh)` : "";
      if (filled.length === 0) toast.warning("A IA não identificou nenhum campo — tente uma imagem mais nítida.", { id: toastId });
      else toast.success(`IA preencheu ${filled.length} campo(s).${note}`, { id: toastId });
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Erro ao ler fatura", { id: toastId });
    } finally {
      setAiExtracting(null);
      if (aiInvoiceRef.current) aiInvoiceRef.current.value = "";
    }
  };

  const handleAIExtractIdDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !_validateAiFile(file)) { e.target.value = ""; return; }
    setAiExtracting("id");
    const toastId = toast.loading("Lendo documento com IA…");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const resp = await axiosInstance.post("/cooperatives/ai/extract-id-document", fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 90000,
      });
      const ex = resp?.data?.extracted || {};
      const patch = {};
      const filled = [];
      if (formData.registration_type === "PJ") {
        if (ex.name) { patch.legal_representative_name = ex.name; filled.push("legal_representative_name"); }
        if (ex.cpf) { patch.legal_representative_cpf = ex.cpf; filled.push("legal_representative_cpf"); }
      } else {
        if (ex.name) { patch.name = ex.name; filled.push("name"); }
        if (ex.cpf) { patch.cpf_cnpj = ex.cpf; filled.push("cpf_cnpj"); }
        if (ex.birth_date) { patch.birth_date = ex.birth_date; filled.push("birth_date"); }
      }
      if (filled.length === 0) toast.warning("A IA não identificou nenhum campo.", { id: toastId });
      else {
        setFormData((prev) => ({ ...prev, ...patch }));
        setAiFilledFields(filled);
        toast.success(`IA (documento) preencheu ${filled.length} campo(s).`, { id: toastId });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Erro ao ler documento", { id: toastId });
    } finally {
      setAiExtracting(null);
      if (aiIdDocRef.current) aiIdDocRef.current.value = "";
    }
  };

  const handleAIExtractCnpj = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !_validateAiFile(file)) { e.target.value = ""; return; }
    setAiExtracting("cnpj");
    const toastId = toast.loading("Lendo cartão CNPJ com IA…");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const resp = await axiosInstance.post("/cooperatives/ai/extract-cnpj-card", fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 90000,
      });
      const ex = resp?.data?.extracted || {};
      const patch = {};
      const filled = [];
      if (ex.corporate_name) { patch.name = ex.corporate_name; filled.push("name"); }
      if (ex.cnpj) { patch.cpf_cnpj = ex.cnpj; filled.push("cpf_cnpj"); }
      if (ex.email) { patch.email = ex.email; filled.push("email"); }
      if (ex.phone) { patch.phone = ex.phone; filled.push("phone"); }
      if (filled.length === 0) toast.warning("A IA não identificou nenhum campo.", { id: toastId });
      else {
        setFormData((prev) => ({ ...prev, ...patch }));
        setAiFilledFields(filled);
        toast.success(`IA (CNPJ) preencheu ${filled.length} campo(s).`, { id: toastId });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Erro ao ler cartão CNPJ", { id: toastId });
    } finally {
      setAiExtracting(null);
      if (aiCnpjRef.current) aiCnpjRef.current.value = "";
    }
  };

  useEffect(() => {
    fetchData();
    fetchDistributorSettings();
  }, []);

  // Preencher automaticamente o parceiro logado (apenas para não-admin)
  useEffect(() => {
    if (partners.length > 0 && user && user.role !== 'admin') {
      const userPartner = partners.find(p => p.email === user.email);
      if (userPartner) {
        setFormData(prev => ({
          ...prev,
          partner_id: userPartner.id
        }));
      }
    }
  }, [partners, user]);

  // Preencher formulário com dados do simulador (via URL)
  useEffect(() => {
    const dataParam = searchParams.get('data');
    
    if (dataParam) {
      try {
        // Decodificar dados do base64
        const decodedData = JSON.parse(atob(dataParam));
        
        // Buscar comissão e desconto da distribuidora
        const distributor = decodedData.distributor || '';
        const discount = distributorDiscounts[distributor] || "";
        let commission = distributorCommissions[distributor] || "";
        
        // Para CEMIG, a comissão depende do desconto (regra por parceiro)
        if (distributor === "CEMIG" && discount) {
          commission = cemigCommission(discount, getCemigRule(user?.email));
        }
        
        // Preencher o formData com os dados recebidos incluindo comissão e desconto
        setFormData(prev => ({
          ...prev,
          name: decodedData.name || '',
          installation_number: decodedData.installation_number || '',
          distributor: distributor,
          phone: decodedData.phone || '',
          registration_type: decodedData.registration_type || 'PF',
          partner_commission: commission,
          tariff_discount: discount
        }));
        
        // Abrir o dialog automaticamente após um pequeno delay
        // para garantir que o componente está montado
        setTimeout(() => {
          setDialogOpen(true);
        }, 100);
        
        // Limpar o parâmetro da URL após usar
        searchParams.delete('data');
        setSearchParams(searchParams);
        
        toast.success("Dados do simulador carregados com sucesso!");
      } catch (error) {
        console.error("Erro ao decodificar dados da URL:", error);
        toast.error("Erro ao carregar dados do simulador");
      }
    }
  }, [searchParams, setSearchParams]);

  // Função para atualizar comissão e desconto ao mudar distribuidora
  const handleDistributorChange = (distributor) => {
    if (!distributor) {
      setFormData({
        ...formData,
        distributor: "",
        partner_commission: "",
        tariff_discount: "",
        agent_number: ""
      });
      return;
    }
    
    const isAutoconsumo = (formData.compensation_modality || '').startsWith('Autoconsumo');
    const discount = isAutoconsumo && autoconsumoDiscounts[distributor]
      ? autoconsumoDiscounts[distributor]
      : (distributorDiscounts[distributor] || "");
    let commission = distributorCommissions[distributor] || "";
    
    if (distributor === "CEMIG" && discount) {
      commission = cemigCommission(discount, cemigRule);
    }
    
    let agentNumber = "274";
    if (distributor === "CEMIG" || distributor === "ENERGISA-MG") {
      agentNumber = "530";
    }
    
    setFormData({
      ...formData,
      distributor: distributor,
      partner_commission: commission,
      tariff_discount: discount,
      agent_number: agentNumber
    });
  };

  const handleCompensationModalityChange = (value) => {
    if (!value) return;
    const dist = formData.distributor;
    let discount = formData.tariff_discount;
    if (dist) {
      discount = (value || '').startsWith('Autoconsumo') && autoconsumoDiscounts[dist]
        ? autoconsumoDiscounts[dist]
        : (distributorDiscounts[dist] || discount);
    }
    
    // Auto "Troca de Modalidade": ao editar uma cooperativa, se a modalidade
    // for alterada em relação à original, marcar situação como "modality_change"
    const updates = {
      ...formData,
      compensation_modality: value,
      tariff_discount: discount
    };
    
    if (editingCoop && editingCoop.compensation_modality && editingCoop.compensation_modality !== value) {
      updates.situation = 'modality_change';
      updates.status = 'modality_change';
      toast.info("Modalidade alterada. Status atualizado automaticamente para \"Troca de Modalidade\".");
    } else if (editingCoop && editingCoop.compensation_modality === value) {
      // Voltou à modalidade original — restaura situação anterior
      updates.situation = editingCoop.situation || 'pending';
      updates.status = editingCoop.status || editingCoop.situation || 'pending';
    }
    
    setFormData(updates);
  };

  const cemigRule = getCemigRule(user?.email, partners.find(p => p.id === formData.partner_id)?.email);

  // Função para recalcular comissão da CEMIG quando desconto mudar
  const handleDiscountChange = (discount) => {
    let commission = formData.partner_commission;
    
    // Para CEMIG, recalcular comissão automaticamente
    if (formData.distributor === "CEMIG" && discount) {
      commission = cemigCommission(discount, cemigRule);
    }
    
    setFormData({
      ...formData,
      tariff_discount: discount,
      partner_commission: commission
    });
  };

  const handleCemigDiscountBlur = () => {
    if (formData.distributor !== "CEMIG" || user?.role === 'admin') return;
    const fixed = clampCemigDiscount(formData.tariff_discount, cemigRule);
    if (fixed !== null) {
      toast.warning(`Desconto CEMIG deve ficar entre ${cemigRule.min}% e ${cemigRule.max}%. Ajustado para ${fixed}%.`);
      setFormData({ ...formData, tariff_discount: String(fixed), partner_commission: cemigCommission(fixed, cemigRule) });
    }
  };

  // Google Drive functions removed - using local storage

  const handleUploadDocuments = async () => {
    if (!rgCpfFileRef.current?.files[0] && !energyBillFileRef.current?.files[0]) {
      toast.error("Selecione pelo menos um documento");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    
    if (rgCpfFileRef.current?.files[0]) {
      formData.append("rg_cpf_file", rgCpfFileRef.current.files[0]);
    }
    if (energyBillFileRef.current?.files[0]) {
      formData.append("energy_bill_file", energyBillFileRef.current.files[0]);
    }

    try {
      // Use local upload endpoint (saves to database as base64)
      await axiosInstance.post(
        `/cooperatives/${selectedCoopId}/upload-documents-local`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      
      toast.success("Documentos salvos com sucesso!");
      
      setUploadDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error("Erro ao enviar documentos:", error);
      const errorMessage = error.response?.data?.detail || error.message || "Erro ao enviar documentos";
      toast.error(typeof errorMessage === 'string' ? errorMessage : "Erro ao enviar documentos");
    } finally {
      setUploading(false);
    }
  };

  const openUploadDialog = (coopId) => {
    setSelectedCoopId(coopId);
    setUploadDialogOpen(true);
  };

  const fetchData = async (search = searchTerm, opts = {}) => {
    const seq = ++fetchSeq.current;
    try {
      setLoading(true);
      const page = opts.page ?? currentPage;
      const isAll = (v) => !v || v === "__all__";
      // Paginação e filtros no servidor: carrega somente a página atual
      const params = {
        page,
        page_size: itemsPerPage,
        ...(search ? { search } : {}),
        ...(!isAll(filterDistributor) ? { distributor: filterDistributor } : {}),
        ...(!isAll(filterStatus) ? { situation: filterStatus } : {}),
        ...(!isAll(filterModality) ? { modality: filterModality } : {}),
        ...(!isAll(filterGroup) ? { consumer_group: filterGroup } : {}),
        ...(!isAll(filterPowerPlant) ? { generator_uc: filterPowerPlant } : {}),
        ...(!isAll(filterPartner) ? { partner_id: filterPartner } : {}),
      };
      
      const requests = [axiosInstance.get("/cooperatives", { params })];
      const needAux = partners.length === 0;
      if (needAux) {
        requests.push(axiosInstance.get("/partners", { params: { limit: 5000 } }));
        if (user?.role === 'admin') requests.push(axiosInstance.get("/powerplants"));
      }
      
      const responses = await Promise.all(requests);
      if (seq !== fetchSeq.current) return;
      
      setCooperatives(responses[0].data);
      const h = responses[0].headers || {};
      setTotalCount(Number(h["x-total-count"] ?? responses[0].data.length));
      setTotalPages(Number(h["x-pages"] ?? 1));
      if (h["x-filters"]) {
        try { setServerFilters(JSON.parse(h["x-filters"])); } catch (_) {}
      }
      if (needAux) {
        setPartners(responses[1].data);
        if (user?.role === 'admin' && responses[2]) setPowerPlants(responses[2].data);
      }
    } catch (error) {
      toast.error("Erro ao carregar dados");
      console.error("Erro ao carregar dados:", error);
    } finally {
      if (seq === fetchSeq.current) setLoading(false);
    }
  };

  // Refaz a busca ao mudar página, tamanho ou filtros
  const filtersKey = [currentPage, itemsPerPage, filterDistributor, filterStatus, filterModality, filterGroup, filterPowerPlant, filterPartner].join("|");
  const filtersMounted = useRef(false);
  useEffect(() => {
    if (!filtersMounted.current) { filtersMounted.current = true; return; } // primeira carga já feita no mount
    fetchData(searchTerm);
  }, [filtersKey]);
  
  const fetchDistributorSettings = async () => {
    try {
      const res = await axiosInstance.get("/distributor-settings");
      const discounts = {};
      const commissions = {};
      const autoDisc = {};
      for (const item of res.data) {
        discounts[item.distributor] = item.discount;
        commissions[item.distributor] = item.partner_commission;
        autoDisc[item.distributor] = item.autoconsumo_discount || 0;
      }
      setDistributorDiscounts(discounts);
      setDistributorCommissions(commissions);
      setAutoconsumoDiscounts(autoDisc);
    } catch (error) {
      console.error("Erro ao carregar configurações de distribuidoras:", error);
    }
  };


  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    // Debounce search
    if (searchTimeout) clearTimeout(searchTimeout);
    const timeout = setTimeout(() => {
      setCurrentPage(1);
      fetchData(value, { page: 1 });
    }, 500);
    setSearchTimeout(timeout);
  };
  
  const [searchTimeout, setSearchTimeout] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar documentos obrigatórios apenas na criação (não na edição)
    if (!editingCoop) {
      if (!newRgCpfFileRef.current?.files[0]) {
        toast.error("RG/CPF é obrigatório");
        return;
      }
      if (formData.registration_type === "PJ" && !newCnpjCardFileRef.current?.files[0]) {
        toast.error("Cartão CNPJ é obrigatório para Pessoa Jurídica");
        return;
      }
    }
    
    // Validar campos obrigatórios gerais
    if (!formData.name) {
      toast.error("Nome é obrigatório");
      return;
    }
    if (!formData.cpf_cnpj) {
      toast.error("CPF/CNPJ é obrigatório");
      return;
    }
    if (!formData.distributor) {
      toast.error("Distribuidora é obrigatória");
      return;
    }
    if (!formData.partner_id) {
      toast.error("Parceiro Comercial é obrigatório");
      return;
    }
    
    // Validar kWh contratado mínimo (200 kWh) e aceitar apenas inteiros
    const contractedKwh = parseInt(formData.contracted_kwh, 10);
    if (isNaN(contractedKwh) || contractedKwh < 200) {
      toast.error("kWh Contratado deve ser no mínimo 200 kWh (apenas números inteiros)");
      return;
    }
    
    // Validar que todas as instalações têm dados completos
    for (let i = 0; i < installations.length; i++) {
      if (!installations[i].installation_number) {
        toast.error(`Número da Instalação ${i + 1} é obrigatório`);
        return;
      }
      if (!installations[i].street) {
        toast.error(`Rua da Instalação ${i + 1} é obrigatória`);
        return;
      }
      if (!installations[i].number) {
        toast.error(`Número do endereço da Instalação ${i + 1} é obrigatório`);
        return;
      }
      if (!installations[i].zip_code) {
        toast.error(`CEP da Instalação ${i + 1} é obrigatório`);
        return;
      }
      if (!installations[i].state) {
        toast.error(`Estado da Instalação ${i + 1} é obrigatório`);
        return;
      }
      if (!installations[i].city) {
        toast.error(`Cidade da Instalação ${i + 1} é obrigatória`);
        return;
      }
      if (!installations[i].neighborhood) {
        toast.error(`Bairro da Instalação ${i + 1} é obrigatório`);
        return;
      }
      // Validar fatura apenas na criação (não na edição)
      if (!editingCoop && !installations[i].energy_bill_file_ref.current?.files[0]) {
        toast.error(`Fatura de Energia da Instalação ${i + 1} é obrigatória`);
        return;
      }
    }
    
    console.log(`📋 Validação concluída. Total de instalações a cadastrar: ${installations.length}`);
    
    try {
      // Criar um cooperado para cada instalação
      const createdCooperatives = [];
      
      for (let i = 0; i < installations.length; i++) {
        const installation = installations[i];
        const formDataToSend = new FormData();
        
        // Dados gerais (iguais para todas as instalações)
        formDataToSend.append('registration_type', formData.registration_type);
        formDataToSend.append('name', formData.name);
        formDataToSend.append('email', formData.email);
        formDataToSend.append('phone', formData.phone);
        formDataToSend.append('cpf_cnpj', formData.cpf_cnpj);
        
        if (formData.registration_type === "PJ") {
          if (formData.legal_representative_cpf) {
            formDataToSend.append('legal_representative_cpf', formData.legal_representative_cpf);
          }
          if (formData.legal_representative_name) {
            formDataToSend.append('legal_representative_name', formData.legal_representative_name);
          }
        }
        
        // Dados da instalação específica
        formDataToSend.append('installation_number', installation.installation_number);
        formDataToSend.append('installation_class', installation.installation_class);
        formDataToSend.append('street', installation.street);
        formDataToSend.append('number', installation.number);
        if (installation.complement) {
          formDataToSend.append('complement', installation.complement);
        }
        formDataToSend.append('zip_code', installation.zip_code);
        formDataToSend.append('state', installation.state);
        formDataToSend.append('city', installation.city);
        formDataToSend.append('neighborhood', installation.neighborhood);
        if (installation.installation_notes) {
          formDataToSend.append('installation_notes', installation.installation_notes);
        }
        
        // Dados compartilhados
        formDataToSend.append('compensation_modality', formData.compensation_modality);
        formDataToSend.append('generator_installation_number', formData.generator_installation_number);
        formDataToSend.append('agent_number', formData.agent_number);
        formDataToSend.append('partner_id', formData.partner_id);
        formDataToSend.append('partner_commission', formData.partner_commission);
        formDataToSend.append('signature_date', new Date().toISOString());
        if (formData.status_date) formDataToSend.append('status_date', `${formData.status_date}T00:00:00`);
        formDataToSend.append('tariff_discount', formData.tariff_discount);
        formDataToSend.append('contracted_kwh', formData.contracted_kwh);
        formDataToSend.append('distributor', formData.distributor);
        formDataToSend.append('distributor_login', formData.distributor_login);
        formDataToSend.append('distributor_password', formData.distributor_password);
        
        if (formData.birth_date) {
          formDataToSend.append('birth_date', new Date(formData.birth_date).toISOString());
        }
        
        if (formData.origin) {
          formDataToSend.append('origin', formData.origin);
        }
        // Status sempre como "pending" para novos cadastros
        formDataToSend.append('status', 'pending');
        
        // Marcar se faz parte de um grupo (múltiplas instalações)
        if (installations.length > 1) {
          formDataToSend.append('is_multiple_installation', 'true');
          formDataToSend.append('installation_index', i.toString());
          formDataToSend.append('total_installations', installations.length.toString());
        }
        
        // Arquivos - RG/CPF e CNPJ devem ser enviados em TODAS as instalações (backend requer)
        if (newRgCpfFileRef.current?.files[0]) {
          formDataToSend.append('rg_cpf_file', newRgCpfFileRef.current.files[0]);
        }
        if (formData.registration_type === "PJ" && newCnpjCardFileRef.current?.files[0]) {
          formDataToSend.append('cnpj_card_file', newCnpjCardFileRef.current.files[0]);
        }
        
        // Fatura de energia específica desta instalação
        if (installation.energy_bill_file_ref.current?.files[0]) {
          formDataToSend.append('energy_bill_file', installation.energy_bill_file_ref.current.files[0]);
        }
        
        console.log(`📝 Enviando instalação ${i + 1}/${installations.length}`);
        console.log(`   UC: ${installation.installation_number}`);
        console.log(`   CPF/CNPJ: ${formData.cpf_cnpj}`);
        console.log(`   Nome: ${formData.name}`);
        
        let response;
        if (editingCoop) {
          // Modo Edição - usa PUT
          console.log(`✏️ Editando cooperado: ${editingCoop.id}`);
          response = await axiosInstance.put(`/cooperatives/${editingCoop.id}`, formDataToSend, {
            headers: { "Content-Type": "multipart/form-data" }
          });
          console.log(`✅ Cooperado atualizado com sucesso! ID: ${response.data.id}`);
        } else {
          // Modo Criação - usa POST
          response = await axiosInstance.post("/cooperatives", formDataToSend, {
            headers: { "Content-Type": "multipart/form-data" }
          });
          console.log(`✅ Instalação ${i + 1} criada com sucesso! ID: ${response.data.id}`);
        }
        
        createdCooperatives.push(response.data);
      }
      
      console.log(`✅ Total de registros ${editingCoop ? 'atualizados' : 'criados'}: ${createdCooperatives.length}`);
      
      if (editingCoop) {
        toast.success("Cooperado atualizado com sucesso!");
      } else if (installations.length > 1) {
        toast.success(`${installations.length} cooperados cadastrados com sucesso!`);
        toast.success(`${installations.length} faturas de energia enviadas!`);
      } else {
        toast.success("Cooperado cadastrado com sucesso!");
      }
      
      if (newRgCpfFileRef.current?.files[0]) {
        toast.success("Documentos enviados!");
      }
      
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error("❌ Erro ao cadastrar cooperado:", error);
      console.error("Detalhes do erro:", error.response?.data);
      const errorMessage = error.response?.data?.detail || error.message || "Erro ao cadastrar cooperado";
      toast.error(typeof errorMessage === 'string' ? errorMessage : "Erro ao cadastrar cooperado");
    }
  };

  const resetForm = () => {
    // Get user's partner if available
    let userPartnerId = "";
    if (partners.length > 0 && user) {
      const userPartner = partners.find(p => p.email === user.email);
      if (userPartner) {
        userPartnerId = userPartner.id;
      }
    }
    
    setFormData({
      registration_type: "PF",
      name: "",
      email: "",
      phone: "",
      cpf_cnpj: "",
      birth_date: "",
      legal_representative_cpf: "",
      legal_representative_name: "",
      installation_number: "",
      installation_class: "",
      compensation_modality: "Geração Compartilhada",
      distributor: "",  // Vazio para forçar seleção
      distributor_login: "",
      distributor_password: "",
      generator_installation_number: "",
      agent_number: "",
      partner_id: userPartnerId,
      partner_commission: "",
      signature_date: "",
      tariff_discount: "",
      contracted_kwh: "",
      street: "",
      number: "",
      complement: "",
      zip_code: "",
      state: "",
      city: "",
      neighborhood: "",
      installation_notes: "",
      origin: "APP",  // Preenchido automaticamente
      situation: "pending"  // Default: Pendente
    });
    
    // Reset installations
    setInstallations([{
      installation_number: "",
      installation_class: "",
      street: "",
      number: "",
      complement: "",
      zip_code: "",
      state: "",
      city: "",
      neighborhood: "",
      installation_notes: "",
      energy_bill_file_ref: React.createRef()
    }]);
    
    // Reset file refs
    if (newRgCpfFileRef.current) newRgCpfFileRef.current.value = "";
    if (newCnpjCardFileRef.current) newCnpjCardFileRef.current.value = "";
    
    // Reset editing state
    setEditingCoop(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir este cooperado?")) return;
    try {
      await axiosInstance.delete(`/cooperatives/${id}`);
      toast.success("Cooperado excluído com sucesso!");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao excluir cooperado");
    }
  };

  const handleGenerateTermoAdesao = async (coop) => {
    try {
      // Termo de Adesão now supports all distributors including CEMIG and Energisa-MG with conditional text
      toast.loading("Gerando Termo de Adesão...");
      
      const response = await axiosInstance.get(`/cooperatives/${coop.id}/termo-adesao`, {
        responseType: 'blob'
      });
      
      // Create download link - usando tipo PDF correto
      const contentType = response.headers['content-type'] || 'application/pdf';
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const disposition = response.headers['content-disposition'] || '';
      const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
      const today = new Date().toLocaleDateString('pt-BR').replace(/\//g, '-');
      const fallbackName = `Termo de Adesão - ${coop.name} - ${coop.distributor || ''} - ${today}.pdf`;
      const filename = utf8Match ? decodeURIComponent(utf8Match[1]) : fallbackName;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.dismiss();
      toast.success("Termo de Adesão gerado com sucesso!");
    } catch (error) {
      toast.dismiss();
      toast.error(error.response?.data?.detail || "Erro ao gerar Termo de Adesão");
    }
  };

  const handleDownloadDocument = async (coopId, documentType, fileName) => {
    try {
      // Use backend endpoint that auto-deletes after download
      const response = await axiosInstance.get(`/cooperatives/${coopId}/download/${documentType}`, {
        responseType: 'blob'
      });
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Download realizado! Documento removido do banco de dados.");
      
      // Refresh data to update UI (document no longer available)
      fetchData();
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error("Documento não encontrado ou já foi baixado anteriormente");
      } else {
        toast.error("Erro ao fazer download do documento");
      }
      console.error("Download error:", error);
    }
  };

  const handleStatusChange = async (coopId, newStatus) => {
    try {
      const params = { status: newStatus };
      // When cancelling, ask the admin to write the cancellation notes
      if (newStatus === 'cancelled') {
        const notes = window.prompt(
          "Observação do Cancelamento\n\nDigite o motivo/observação para o cancelamento deste cooperado. Este texto será salvo junto ao registro e aparecerá no CSV."
        );
        if (notes === null) {
          // user cancelled the prompt
          toast.info("Cancelamento não aplicado.");
          return;
        }
        params.cancellation_notes = notes.trim();
      }
      // Usar o novo endpoint PATCH específico para status
      await axiosInstance.patch(`/cooperatives/${coopId}/status`, null, {
        params
      });
      
      if (newStatus === 'active') {
        toast.success("Cooperado ativado! Data de assinatura registrada automaticamente.");
      } else if (newStatus === 'sent_for_signature') {
        toast.success("Status alterado para: Enviado para Assinatura");
      } else if (newStatus === 'cancelled') {
        toast.success("Cooperado cancelado");
      } else if (newStatus === 'pending') {
        toast.success("Status alterado para: Pendente");
      } else {
        toast.success("Status atualizado com sucesso!");
      }
      
      // Reload data to show changes
      fetchData(searchTerm);
    } catch (error) {
      console.error("Erro ao atualizar status:", error);
      console.error("Detalhes do erro:", error.response?.data);
      
      const errorDetail = error.response?.data?.detail;
      if (errorDetail) {
        toast.error(`Erro: ${errorDetail}`);
      } else {
        toast.error("Erro ao atualizar status");
      }
    }
  };
  const handlePartnerChange = async (coopId, newPartnerId) => {
    try {
      // Buscar dados do cooperado atual
      const coop = cooperatives.find(c => c.id === coopId);
      if (!coop) {
        toast.error("Cooperado não encontrado");
        return;
      }

      // Buscar nome do novo parceiro
      const partner = partners.find(p => p.id === newPartnerId);
      if (!partner) {
        toast.error("Parceiro não encontrado");
        return;
      }

      // Atualizar apenas o partner_id e partner_name
      await axiosInstance.patch(`/cooperatives/${coopId}/partner`, {
        partner_id: newPartnerId,
        partner_name: partner.name
      });

      toast.success(`Parceiro alterado para: ${partner.name}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao atualizar parceiro");
      console.error(error);
    }
  };

  const handleImportCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImporting(true);
    setImportErrors([]);
    setImportId(null);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axiosInstance.post("/cooperatives/import-csv", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      toast.success(`${response.data.imported} cooperados importados!`);
      if (response.data.errors && response.data.errors.length > 0) {
        setImportErrors(response.data.errors);
        setImportId(response.data.import_id);
        toast.warning(`${response.data.errors.length} erros encontrados. Clique em "Baixar Erros" para ver detalhes.`);
      }
      fetchData();
    } catch (error) {
      toast.error("Erro ao importar CSV");
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDownloadErrors = async () => {
    if (!importId) return;

    try {
      const response = await axiosInstance.get(`/cooperatives/export-import-errors/${importId}`, {
        responseType: "blob"
      });

      const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `erros_importacao_cooperados_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Erros exportados com sucesso");
    } catch (error) {
      console.error("Error downloading errors:", error);
      toast.error("Erro ao baixar arquivo de erros");
    }
  };

  const handleUcUpdateCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const response = await axiosInstance.post("/cooperatives/update-uc-csv", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      const { updated_cooperatives, updated_invoices, not_found, duplicates, total_errors } = response.data;
      toast.success(
        `${updated_cooperatives} cooperado(s) e ${updated_invoices} fatura(s) atualizadas.` +
        (not_found ? ` ${not_found} UC antiga(s) não encontrada(s).` : "") +
        (duplicates ? ` ${duplicates} UC nova(s) já em uso.` : "")
      );
      if (total_errors > 0 && response.data.errors?.length) {
        console.warn("UC update errors:", response.data.errors);
        toast.warning(`${total_errors} linhas com erro. Verifique o console para detalhes.`);
      }
      fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erro ao atualizar UCs por CSV");
    } finally {
      if (ucFileInputRef.current) ucFileInputRef.current.value = "";
    }
  };

  const handleDownloadUcTemplate = () => {
    const csv = "\ufeffNúmero de Instalação;Número de Instalação Novo\n12345678;87654321\n";
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "modelo_atualizacao_uc.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleCleanupData = async () => {
    if (!window.confirm('Deseja limpar dados corrompidos (remover apóstrofos de CPF, telefone, etc.)?\n\nIsso corrigirá dados que foram importados incorretamente do Excel.')) {
      return;
    }
    
    try {
      const response = await axiosInstance.post("/cooperatives/cleanup-data");
      toast.success(`Limpeza concluída! ${response.data.updated} registros atualizados.`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao limpar dados");
    }
  };

  const handleExportCSV = async () => {
    if (!ensureCsvPassword()) return;
    try {
      // Construir parâmetros com filtros ativos
      const params = {};
      
      if (filterDistributor && filterDistributor !== "__all__") {
        params.distributor = filterDistributor;
      }
      if (filterGroup && filterGroup !== "__all__") {
        params.consumer_group = filterGroup;
      }
      
      if (filterPowerPlant && filterPowerPlant !== "__all__") {
        params.generator_installation_number = filterPowerPlant;
      }
      
      if (filterStatus && filterStatus !== "__all__") {
        params.situation = filterStatus;
      }
      
      if (searchTerm) {
        params.search = searchTerm;
      }
      
      const response = await axiosInstance.get("/cooperatives/export-csv", {
        params: params,
        responseType: "blob"
      });
      
      // Adicionar info de filtros no nome do arquivo
      let filename = `cooperados_${new Date().toISOString().split('T')[0]}`;
      if (Object.keys(params).length > 0) {
        filename += '_filtrado';
      }
      filename += '.csv';
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      const numFiltros = Object.keys(params).length;
      if (numFiltros > 0) {
        toast.success(`CSV exportado com ${numFiltros} filtro(s) aplicado(s)!`);
      } else {
        toast.success("CSV exportado com sucesso!");
      }
    } catch (error) {
      toast.error("Erro ao exportar CSV");
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axiosInstance.get("/cooperatives/template", {
        responseType: "blob"
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "modelo_cooperados.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Modelo baixado com sucesso!");
    } catch (error) {
      toast.error("Erro ao baixar modelo");
    }
  };


  const handleSelectAll = (e) => {
    if (e.target.checked) {
      // Select all cooperatives on current page
      const pageCoopIds = cooperatives.map(c => c.id);
      setSelectedCoops(pageCoopIds);
    } else {
      setSelectedCoops([]);
    }
  };

  const handleSelectCoop = (coopId) => {
    setSelectedCoops(prev => {
      if (prev.includes(coopId)) {
        return prev.filter(id => id !== coopId);
      } else {
        return [...prev, coopId];
      }
    });
  };

  const handleDeleteSelected = async () => {
    if (selectedCoops.length === 0) {
      toast.warning("Nenhum cooperado selecionado");
      return;
    }

    if (!window.confirm(`Tem certeza que deseja deletar ${selectedCoops.length} cooperado(s)?`)) {
      return;
    }

    try {
      await axiosInstance.post("/cooperatives/bulk/delete", selectedCoops);
      toast.success(`${selectedCoops.length} cooperado(s) deletado(s) com sucesso!`);
      setSelectedCoops([]);
      fetchData(searchTerm);
    } catch (error) {
      toast.error("Erro ao deletar cooperados selecionados");
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm("⚠️ ATENÇÃO: Tem certeza que deseja deletar TODOS os cooperados? Esta ação não pode ser desfeita!")) {
      return;
    }

    if (!window.confirm("⚠️ ÚLTIMA CONFIRMAÇÃO: Todos os cooperados serão permanentemente deletados. Continuar?")) {
      return;
    }

    try {
      const response = await axiosInstance.delete("/cooperatives/bulk/all");
      toast.success(response.data.message);
      setSelectedCoops([]);
      fetchData(searchTerm);
    } catch (error) {
      toast.error("Erro ao deletar todos os cooperados");
    }
  };


  const getPartnerName = (partnerId) => {
    const partner = partners.find(p => p.id === partnerId);
    return partner ? partner.name : "-";
  };

  // Aplicar filtros aos cooperados
  const filteredCooperatives = cooperatives
    .filter(coop => {
      if (filterDistributor && filterDistributor !== "__all__" && (coop.distributor || '').trim().toUpperCase() !== filterDistributor.trim().toUpperCase()) return false;
      
      // Filtro por usina
      if (filterPowerPlant && filterPowerPlant !== "__all__") {
        if (filterPowerPlant === "__empty__") {
          // Filtrar cooperados SEM usina
          if (coop.generator_installation_number && coop.generator_installation_number.trim() !== '') return false;
        } else {
          // Filtrar cooperados COM usina específica
          if (coop.generator_installation_number !== filterPowerPlant) return false;
        }
      }
      
      // Filtro por status
      if (filterStatus && filterStatus !== "__all__" && coop.situation !== filterStatus) return false;
      
      // Filtro por parceiro
      if (filterPartner && filterPartner !== "__all__" && coop.partner_id !== filterPartner) return false;

      // Filtro por modalidade de compensação
      if (filterModality && filterModality !== "__all__" && (coop.compensation_modality || "") !== filterModality) return false;

      // Filtro por grupo (A/B) — registros sem o campo contam como Grupo B
      if (filterGroup && filterGroup !== "__all__" && ((coop.consumer_group === "Grupo A") ? "Grupo A" : "Grupo B") !== filterGroup) return false;

      return true;
    })
    .sort((a, b) => {
      // Ordenar por data de criação (mais recente primeiro)
      const dateA = new Date(a.created_at || 0);
      const dateB = new Date(b.created_at || 0);
      return dateB - dateA;
    });

  // Obter lista única de distribuidoras (case-insensitive — display em UPPERCASE)
  const uniqueDistributors = serverFilters.distributors || [];

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl md:text-4xl font-bold text-gray-800 mb-2">Cooperados</h1>
            <p className="text-gray-600">Gerencie os cooperados cadastrados</p>
          </div>
          <div className="flex gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleImportCSV}
              className="hidden"
            />
            <input
              ref={ucFileInputRef}
              type="file"
              accept=".csv"
              onChange={handleUcUpdateCSV}
              className="hidden"
            />
            {user?.role === 'admin' && (
              <>
                <Button
                  onClick={handleDownloadTemplate}
                  className="bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Baixar Modelo
                </Button>
                <Button
                  data-testid="import-csv-button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={importing}
                  className="bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {importing ? "Importando..." : "Importar CSV"}
                </Button>
                {importErrors.length > 0 && (
                  <Button
                    onClick={handleDownloadErrors}
                    className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Baixar Erros ({importErrors.length})
                  </Button>
                )}
                <Button
                  data-testid="export-csv-button"
                  onClick={handleExportCSV}
                  className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Exportar CSV
                </Button>
                <Button
                  data-testid="uc-template-button"
                  onClick={handleDownloadUcTemplate}
                  className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700"
                  title="Baixar modelo de atualização de UC"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Modelo UC
                </Button>
                <Button
                  data-testid="update-uc-csv-button"
                  onClick={() => ucFileInputRef.current?.click()}
                  className="bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700"
                  title="Atualizar Número de UC por CSV"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Atualizar UC por CSV
                </Button>
                {selectedCoops.length > 0 && (
                  <Button
                    onClick={handleDeleteSelected}
                    className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Deletar Selecionados ({selectedCoops.length})
                  </Button>
                )}
                <Button
                  onClick={handleDeleteAll}
                  variant="outline"
                  className="border-red-500 text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Deletar Todos
                </Button>
                <Button
                  onClick={handleCleanupData}
                  variant="outline"
                  className="border-yellow-500 text-yellow-600 hover:bg-yellow-50"
                  title="Remove apóstrofos de CPF, telefone e outros campos que podem ter sido corrompidos na importação"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Limpar Dados
                </Button>
              </>
            )}
            
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) {
                // Ao fechar, limpar e resetar após um delay
                setTimeout(() => {
                  setEditingCoop(null);
                  setDocuments({ rg_cpf_file: null, cnpj_card_file: null });
                  resetForm();
                }, 100);
              }
            }}>
              <DialogContent className="max-w-4xl w-[95vw] max-h-[85vh] overflow-y-auto sm:max-h-[90vh]">
                <DialogHeader>
                  <DialogTitle>{editingCoop ? 'Editar Cooperado' : 'Cadastrar Novo Cooperado'}</DialogTitle>
                  <DialogDescription>
                    {editingCoop ? 'Edite os campos necessários e salve as alterações.' : 'Preencha os dados do cooperado para realizar o cadastro no sistema.'}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* ─── Bloco: Preenchimento automático com IA ─── */}
                  <div className="rounded-lg border-2 border-purple-300 bg-gradient-to-r from-purple-50 via-fuchsia-50 to-pink-50 p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Sparkles className="w-4 h-4 text-purple-700" />
                      <span className="text-sm font-semibold text-purple-800">Preenchimento automático com IA</span>
                      <span className="text-[10px] px-2 py-0.5 bg-purple-200 text-purple-900 rounded-full">Gemini 3 Flash</span>
                    </div>
                    <p className="text-[11px] text-purple-700/80 mb-3">
                      Envie a fatura, um documento com foto (RG/CNH) ou o Cartão CNPJ (PJ) — a IA lê e preenche os campos automaticamente. <br />
                      <strong>Fatura:</strong> o campo <em>kW contratado</em> é preenchido com a <strong>média anual do histórico de consumo</strong>.
                    </p>

                    <input ref={aiInvoiceRef} type="file" accept="application/pdf,image/jpeg,image/jpg,image/png,image/webp" className="hidden" onChange={handleAIExtractInvoice} data-testid="coop-ai-invoice-input" />
                    <input ref={aiIdDocRef} type="file" accept="application/pdf,image/jpeg,image/jpg,image/png,image/webp" className="hidden" onChange={handleAIExtractIdDoc} data-testid="coop-ai-id-input" />
                    <input ref={aiCnpjRef} type="file" accept="application/pdf,image/jpeg,image/jpg,image/png,image/webp" className="hidden" onChange={handleAIExtractCnpj} data-testid="coop-ai-cnpj-input" />

                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        onClick={() => aiInvoiceRef.current?.click()}
                        disabled={aiExtracting !== null}
                        data-testid="coop-ai-invoice-btn"
                        className="bg-white hover:bg-purple-50 text-purple-800 border border-purple-300 h-9 px-3 text-xs"
                      >
                        <FileImage className="w-3.5 h-3.5 mr-1.5" />
                        {aiExtracting === "invoice" ? "Lendo fatura…" : "Ler Fatura"}
                      </Button>

                      <Button
                        type="button"
                        onClick={() => aiIdDocRef.current?.click()}
                        disabled={aiExtracting !== null}
                        data-testid="coop-ai-id-btn"
                        className="bg-white hover:bg-purple-50 text-purple-800 border border-purple-300 h-9 px-3 text-xs"
                        title={formData.registration_type === "PJ"
                          ? "Documento com foto do REPRESENTANTE LEGAL (PJ)"
                          : "Envie RG, CNH ou CIN do titular"}
                      >
                        <IdCard className="w-3.5 h-3.5 mr-1.5" />
                        {aiExtracting === "id"
                          ? "Lendo documento…"
                          : (formData.registration_type === "PJ" ? "Ler Doc. Representante" : "Ler Documento c/ Foto")}
                      </Button>

                      {formData.registration_type === "PJ" && (
                        <Button
                          type="button"
                          onClick={() => aiCnpjRef.current?.click()}
                          disabled={aiExtracting !== null}
                          data-testid="coop-ai-cnpj-btn"
                          className="bg-white hover:bg-purple-50 text-purple-800 border border-purple-300 h-9 px-3 text-xs"
                        >
                          <Building2 className="w-3.5 h-3.5 mr-1.5" />
                          {aiExtracting === "cnpj" ? "Lendo cartão CNPJ…" : "Ler Cartão CNPJ"}
                        </Button>
                      )}
                    </div>

                    {aiFilledFields.length > 0 && (
                      <div className="mt-3 text-[11px] text-purple-800 font-medium" data-testid="coop-ai-filled-summary">
                        ✨ IA preencheu {aiFilledFields.length} campo(s): {aiFilledFields.slice(0, 8).join(", ")}{aiFilledFields.length > 8 ? "…" : ""} — revise antes de salvar.
                      </div>
                    )}
                  </div>

                  {/* Tipo de Cadastro */}
                  <div className="space-y-2">
                    <Label>Tipo de Cadastro</Label>
                    <Select value={formData.registration_type} onValueChange={(value) => setFormData(prev => ({ ...prev, registration_type: value }))}>
                      <SelectTrigger data-testid="registration-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="PF">Pessoa Física</SelectItem>
                        <SelectItem value="PJ">Pessoa Jurídica</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Dados Básicos */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Nome do Cooperado *</Label>
                      <Input data-testid="coop-name" value={formData.name} onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))} required />
                    </div>
                    <div className="space-y-2">
                      <Label>E-mail *</Label>
                      <Input data-testid="coop-email" type="email" value={formData.email} onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))} required />
                    </div>
                    <div className="space-y-2">
                      <Label>Telefone *</Label>
                      <Input data-testid="coop-phone" value={formData.phone} onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))} required />
                    </div>
                    <div className="space-y-2">
                      <Label>CPF/CNPJ *</Label>
                      <Input data-testid="coop-cpf-cnpj" value={formData.cpf_cnpj} onChange={(e) => setFormData(prev => ({ ...prev, cpf_cnpj: e.target.value }))} required />
                    </div>
                    <div className="space-y-2">
                      <Label>Data de Nascimento</Label>
                      <Input type="date" value={formData.birth_date} onChange={(e) => setFormData(prev => ({ ...prev, birth_date: e.target.value }))} />
                    </div>
                  </div>

                  {/* Campos PJ */}
                  {formData.registration_type === "PJ" && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-blue-50 rounded-lg">
                      <div className="space-y-2">
                        <Label>CPF do Representante Legal</Label>
                        <Input value={formData.legal_representative_cpf} onChange={(e) => setFormData(prev => ({ ...prev, legal_representative_cpf: e.target.value }))} />
                      </div>
                      <div className="space-y-2">
                        <Label>Nome do Representante Legal</Label>
                        <Input value={formData.legal_representative_name} onChange={(e) => setFormData(prev => ({ ...prev, legal_representative_name: e.target.value }))} />
                      </div>
                    </div>
                  )}

                  {/* Dados Gerais da Instalação */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-lg">Dados Gerais da Instalação</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Grupo</Label>
                        <Input value={formData.consumer_group || "Grupo B"} readOnly className="bg-gray-100 cursor-not-allowed" data-testid="edit-coop-consumer-group" />
                        <p className="text-xs text-gray-500">Definido no cadastro (Grupo A somente via Simulador Grupo A)</p>
                      </div>
                      <div className="space-y-2">
                        <Label>Data Status</Label>
                        <Input type="date" value={formData.status_date || ""} onChange={(e) => setFormData({ ...formData, status_date: e.target.value })} data-testid="edit-coop-status-date" />
                        <p className="text-xs text-gray-500">Data da última alteração de status (preenchida automaticamente; ajuste para registros antigos)</p>
                      </div>
                      <div className="space-y-2">
                        <Label>Modalidade de Compensação *</Label>
                        <Select value={formData.compensation_modality} onValueChange={handleCompensationModalityChange}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Geração Compartilhada">Geração Compartilhada</SelectItem>
                            <SelectItem value="Autoconsumo Remoto">Autoconsumo Remoto</SelectItem>
                            <SelectItem value="Autoconsumo C">Autoconsumo C</SelectItem>
                            {user?.role === 'admin' && (
                              <SelectItem value="Autoconsumo Remoto - Usina">Autoconsumo Remoto - Usina</SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Distribuidora *</Label>
                        <Select value={formData.distributor} onValueChange={handleDistributorChange}>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione..." />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.keys(distributorDiscounts).sort().map((d) => (
                              <SelectItem key={d} value={d}>{d}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {formData.distributor && (
                          <p className="text-xs text-gray-500">
                            {formData.distributor === 'CEMIG' 
                              ? `Comissão = ${cemigRule.max} − Desconto (%) · faixa ${cemigRule.min}–${cemigRule.max}%` 
                              : 'Comissão e desconto automáticos'}
                          </p>
                        )}
                      </div>
                      {formData.distributor && formData.distributor !== 'CEMIG' && (
                        <>
                          <div className="space-y-2">
                            <Label>Login da Distribuidora *</Label>
                            <Input value={formData.distributor_login} onChange={(e) => setFormData(prev => ({ ...prev, distributor_login: e.target.value }))} required />
                          </div>
                          <div className="space-y-2">
                            <Label>Senha da Distribuidora *</Label>
                            <div className="relative">
                              <Input 
                                type={showPassword ? "text" : "password"} 
                                value={formData.distributor_password} 
                                onChange={(e) => setFormData(prev => ({ ...prev, distributor_password: e.target.value }))} 
                                required 
                                className="pr-10"
                              />
                              <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                              >
                                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                              </button>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Parceiro e Comissão */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-lg">Parceiro e Comissão</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Parceiro Comercial *</Label>
                        <Select 
                          value={formData.partner_id} 
                          onValueChange={(value) => setFormData(prev => ({ ...prev, partner_id: value }))} 
                          disabled={user?.role !== 'admin'}
                        >
                          <SelectTrigger className={user?.role !== 'admin' ? 'bg-gray-100' : ''}>
                            <SelectValue placeholder="Selecione o parceiro" />
                          </SelectTrigger>
                          <SelectContent>
                            {partners.map(p => (
                              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {user?.role !== 'admin' && (
                          <p className="text-xs text-gray-500">Preenchido automaticamente com seu parceiro</p>
                        )}
                        {user?.role === 'admin' && (
                          <p className="text-xs text-blue-600">Selecione o parceiro responsável pelo cooperado</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label>Comissão do Parceiro (%) *</Label>
                        <Input 
                          type="number" 
                          step="0.01" 
                          value={formData.partner_commission} 
                          className="bg-gray-100" 
                          readOnly 
                          required 
                        />
                        <p className="text-xs text-gray-500">
                          {formData.distributor === 'CEMIG' 
                            ? `Calculada como: ${cemigRule.max} − Desconto (%)` 
                            : 'Definida automaticamente pela distribuidora'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Contrato */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-lg">Dados do Contrato</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Desconto na Tarifa (%) *</Label>
                        <Input 
                          type="number" 
                          step="0.01" 
                          value={formData.tariff_discount} 
                          onChange={(e) => handleDiscountChange(e.target.value)} 
                          onBlur={handleCemigDiscountBlur}
                          readOnly={formData.distributor !== 'CEMIG' && user?.role !== 'admin'}
                          className={(formData.distributor !== 'CEMIG' && user?.role !== 'admin') ? 'bg-gray-100 cursor-not-allowed' : ''}
                          required 
                        />
                        <p className="text-xs text-gray-500">
                          {user?.role === 'admin'
                            ? '🔓 Admin: Desconto editável para todas as distribuidoras'
                            : formData.distributor === 'CEMIG' 
                            ? `Editável entre ${cemigRule.min}% e ${cemigRule.max}% - a comissão é recalculada automaticamente` 
                            : 'Automático - Somente editável para CEMIG'}
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label>kWh Contratado *</Label>
                        <Input 
                          type="number" 
                          step="1" 
                          min="200"
                          value={formData.contracted_kwh} 
                          onChange={(e) => setFormData(prev => ({ ...prev, contracted_kwh: Math.floor(e.target.value) || '' }))} 
                          required 
                          placeholder="Mínimo 200"
                        />
                        <p className="text-xs text-gray-500">Mínimo: 200 kWh (apenas números inteiros)</p>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 italic">
                      📅 A data de assinatura será registrada automaticamente quando o admin ativar o cooperado.
                    </p>
                  </div>

                  {/* Instalação Principal */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-lg">Dados da Instalação Principal</h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Número da Instalação (UC) *</Label>
                        <Input
                          value={installations[0].installation_number}
                          onChange={(e) => updateInstallation(0, 'installation_number', e.target.value)}
                          required
                          placeholder="Ex: 123456789"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Classe da Instalação</Label>
                        <Select
                          value={installations[0].installation_class}
                          onValueChange={(value) => updateInstallation(0, 'installation_class', value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Comercial Monofásico">Comercial Monofásico</SelectItem>
                            <SelectItem value="Residencial Monofásico">Residencial Monofásico</SelectItem>
                            <SelectItem value="Industrial Monofásico">Industrial Monofásico</SelectItem>
                            <SelectItem value="Poder Público Monofásico">Poder Público Monofásico</SelectItem>
                            <SelectItem value="Rural Monofásico">Rural Monofásico</SelectItem>
                            <SelectItem value="Comercial Bifásico">Comercial Bifásico</SelectItem>
                            <SelectItem value="Residencial Bifásico">Residencial Bifásico</SelectItem>
                            <SelectItem value="Industrial Bifásico">Industrial Bifásico</SelectItem>
                            <SelectItem value="Poder Público Bifásico">Poder Público Bifásico</SelectItem>
                            <SelectItem value="Rural Bifásico">Rural Bifásico</SelectItem>
                            <SelectItem value="Comercial Trifásico">Comercial Trifásico</SelectItem>
                            <SelectItem value="Residencial Trifásico">Residencial Trifásico</SelectItem>
                            <SelectItem value="Industrial Trifásico">Industrial Trifásico</SelectItem>
                            <SelectItem value="Poder Público Trifásico">Poder Público Trifásico</SelectItem>
                            <SelectItem value="Rural Trifásico">Rural Trifásico</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Rua *</Label>
                        <Input
                          value={installations[0].street}
                          onChange={(e) => updateInstallation(0, 'street', e.target.value)}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Número *</Label>
                        <Input
                          value={installations[0].number}
                          onChange={(e) => updateInstallation(0, 'number', e.target.value)}
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Complemento</Label>
                        <Input
                          value={installations[0].complement}
                          onChange={(e) => updateInstallation(0, 'complement', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>CEP *</Label>
                        <Input
                          value={installations[0].zip_code}
                          onChange={(e) => updateInstallation(0, 'zip_code', e.target.value)}
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>UF *</Label>
                        <Select
                          value={installations[0].state}
                          onValueChange={(value) => updateInstallation(0, 'state', value)}
                          required
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="UF" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="AC">AC</SelectItem>
                            <SelectItem value="AL">AL</SelectItem>
                            <SelectItem value="AP">AP</SelectItem>
                            <SelectItem value="AM">AM</SelectItem>
                            <SelectItem value="BA">BA</SelectItem>
                            <SelectItem value="CE">CE</SelectItem>
                            <SelectItem value="DF">DF</SelectItem>
                            <SelectItem value="ES">ES</SelectItem>
                            <SelectItem value="GO">GO</SelectItem>
                            <SelectItem value="MA">MA</SelectItem>
                            <SelectItem value="MT">MT</SelectItem>
                            <SelectItem value="MS">MS</SelectItem>
                            <SelectItem value="MG">MG</SelectItem>
                            <SelectItem value="PA">PA</SelectItem>
                            <SelectItem value="PB">PB</SelectItem>
                            <SelectItem value="PR">PR</SelectItem>
                            <SelectItem value="PE">PE</SelectItem>
                            <SelectItem value="PI">PI</SelectItem>
                            <SelectItem value="RJ">RJ</SelectItem>
                            <SelectItem value="RN">RN</SelectItem>
                            <SelectItem value="RS">RS</SelectItem>
                            <SelectItem value="RO">RO</SelectItem>
                            <SelectItem value="RR">RR</SelectItem>
                            <SelectItem value="SC">SC</SelectItem>
                            <SelectItem value="SP">SP</SelectItem>
                            <SelectItem value="SE">SE</SelectItem>
                            <SelectItem value="TO">TO</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Cidade *</Label>
                        <Input
                          value={installations[0].city}
                          onChange={(e) => updateInstallation(0, 'city', e.target.value)}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Bairro *</Label>
                        <Input
                          value={installations[0].neighborhood}
                          onChange={(e) => updateInstallation(0, 'neighborhood', e.target.value)}
                          required
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Observações desta Instalação</Label>
                      <Textarea
                        value={installations[0].installation_notes}
                        onChange={(e) => updateInstallation(0, 'installation_notes', e.target.value)}
                        rows={2}
                        placeholder="Observações específicas desta instalação"
                      />
                    </div>

                    {/* Upload de fatura da instalação principal */}
                    <div className="space-y-2 border-t pt-4">
                      <Label htmlFor="energy-bill-0" className="text-blue-700 font-semibold">
                        Cópia da Fatura de Energia <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="energy-bill-0"
                        ref={installations[0].energy_bill_file_ref}
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        className="cursor-pointer"
                        required
                      />
                      <p className="text-xs text-gray-500">PDF, JPG ou PNG (obrigatório)</p>
                    </div>
                  </div>

                  {/* Instalações Adicionais (apenas se houver mais de 1) */}
                  {installations.length > 1 && (
                    <div className="space-y-4">
                      <h3 className="font-semibold text-lg">Instalações Adicionais</h3>
                      {installations.slice(1).map((installation, idx) => {
                        const index = idx + 1; // índice real no array
                        return (
                          <Card key={index} className="border-2 border-blue-100 bg-blue-50/30">
                            <CardContent className="pt-6">
                              <div className="space-y-4">
                                <div className="flex justify-between items-center mb-2">
                                  <h4 className="font-semibold text-blue-700">Instalação {index + 1}</h4>
                                  <Button
                                    type="button"
                                    onClick={() => removeInstallation(index)}
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  >
                                    Remover
                                  </Button>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div className="space-y-2">
                                    <Label>Número da Instalação (UC) *</Label>
                                    <Input
                                      value={installation.installation_number}
                                      onChange={(e) => updateInstallation(index, 'installation_number', e.target.value)}
                                      required
                                      placeholder="Ex: 123456789"
                                    />
                                  </div>
                                  <div className="space-y-2">
                                    <Label>Classe da Instalação</Label>
                                    <Select
                                      value={installation.installation_class}
                                      onValueChange={(value) => updateInstallation(index, 'installation_class', value)}
                                    >
                                      <SelectTrigger>
                                        <SelectValue placeholder="Selecione..." />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="Comercial Monofásico">Comercial Monofásico</SelectItem>
                                        <SelectItem value="Residencial Monofásico">Residencial Monofásico</SelectItem>
                                        <SelectItem value="Industrial Monofásico">Industrial Monofásico</SelectItem>
                                        <SelectItem value="Poder Público Monofásico">Poder Público Monofásico</SelectItem>
                                        <SelectItem value="Rural Monofásico">Rural Monofásico</SelectItem>
                                        <SelectItem value="Comercial Bifásico">Comercial Bifásico</SelectItem>
                                        <SelectItem value="Residencial Bifásico">Residencial Bifásico</SelectItem>
                                        <SelectItem value="Industrial Bifásico">Industrial Bifásico</SelectItem>
                                        <SelectItem value="Poder Público Bifásico">Poder Público Bifásico</SelectItem>
                                        <SelectItem value="Rural Bifásico">Rural Bifásico</SelectItem>
                                        <SelectItem value="Comercial Trifásico">Comercial Trifásico</SelectItem>
                                        <SelectItem value="Residencial Trifásico">Residencial Trifásico</SelectItem>
                                        <SelectItem value="Industrial Trifásico">Industrial Trifásico</SelectItem>
                                        <SelectItem value="Poder Público Trifásico">Poder Público Trifásico</SelectItem>
                                        <SelectItem value="Rural Trifásico">Rural Trifásico</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div className="space-y-2">
                                    <Label>Rua *</Label>
                                    <Input
                                      value={installation.street}
                                      onChange={(e) => updateInstallation(index, 'street', e.target.value)}
                                      required
                                    />
                                  </div>
                                  <div className="space-y-2">
                                    <Label>Número *</Label>
                                    <Input
                                      value={installation.number}
                                      onChange={(e) => updateInstallation(index, 'number', e.target.value)}
                                      required
                                    />
                                  </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div className="space-y-2">
                                    <Label>Complemento</Label>
                                    <Input
                                      value={installation.complement}
                                      onChange={(e) => updateInstallation(index, 'complement', e.target.value)}
                                    />
                                  </div>
                                  <div className="space-y-2">
                                    <Label>CEP *</Label>
                                    <Input
                                      value={installation.zip_code}
                                      onChange={(e) => updateInstallation(index, 'zip_code', e.target.value)}
                                      required
                                    />
                                  </div>
                                </div>

                                <div className="grid grid-cols-3 gap-4">
                                  <div className="space-y-2">
                                    <Label>UF *</Label>
                                    <Select
                                      value={installation.state}
                                      onValueChange={(value) => updateInstallation(index, 'state', value)}
                                      required
                                    >
                                      <SelectTrigger>
                                        <SelectValue placeholder="UF" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="AC">AC</SelectItem>
                                        <SelectItem value="AL">AL</SelectItem>
                                        <SelectItem value="AP">AP</SelectItem>
                                        <SelectItem value="AM">AM</SelectItem>
                                        <SelectItem value="BA">BA</SelectItem>
                                        <SelectItem value="CE">CE</SelectItem>
                                        <SelectItem value="DF">DF</SelectItem>
                                        <SelectItem value="ES">ES</SelectItem>
                                        <SelectItem value="GO">GO</SelectItem>
                                        <SelectItem value="MA">MA</SelectItem>
                                        <SelectItem value="MT">MT</SelectItem>
                                        <SelectItem value="MS">MS</SelectItem>
                                        <SelectItem value="MG">MG</SelectItem>
                                        <SelectItem value="PA">PA</SelectItem>
                                        <SelectItem value="PB">PB</SelectItem>
                                        <SelectItem value="PR">PR</SelectItem>
                                        <SelectItem value="PE">PE</SelectItem>
                                        <SelectItem value="PI">PI</SelectItem>
                                        <SelectItem value="RJ">RJ</SelectItem>
                                        <SelectItem value="RN">RN</SelectItem>
                                        <SelectItem value="RS">RS</SelectItem>
                                        <SelectItem value="RO">RO</SelectItem>
                                        <SelectItem value="RR">RR</SelectItem>
                                        <SelectItem value="SC">SC</SelectItem>
                                        <SelectItem value="SP">SP</SelectItem>
                                        <SelectItem value="SE">SE</SelectItem>
                                        <SelectItem value="TO">TO</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  <div className="space-y-2">
                                    <Label>Cidade *</Label>
                                    <Input
                                      value={installation.city}
                                      onChange={(e) => updateInstallation(index, 'city', e.target.value)}
                                      required
                                    />
                                  </div>
                                  <div className="space-y-2">
                                    <Label>Bairro *</Label>
                                    <Input
                                      value={installation.neighborhood}
                                      onChange={(e) => updateInstallation(index, 'neighborhood', e.target.value)}
                                      required
                                    />
                                  </div>
                                </div>

                                <div className="space-y-2">
                                  <Label>Observações desta Instalação</Label>
                                  <Textarea
                                    value={installation.installation_notes}
                                    onChange={(e) => updateInstallation(index, 'installation_notes', e.target.value)}
                                    rows={2}
                                    placeholder="Observações específicas desta instalação"
                                  />
                                </div>

                                {/* Upload de fatura específica desta instalação */}
                                <div className="space-y-2 border-t pt-4">
                                  <Label htmlFor={`energy-bill-${index}`} className="text-blue-700 font-semibold">
                                    Cópia da Fatura de Energia desta UC {!editingCoop && <span className="text-red-500">*</span>}
                                  </Label>
                                  {editingCoop && installation.has_energy_bill && (
                                    <p className="text-xs text-green-600 mb-1">
                                      ✓ Fatura já enviada. Faça novo upload apenas se desejar substituir.
                                    </p>
                                  )}
                                  <Input
                                    id={`energy-bill-${index}`}
                                    ref={installation.energy_bill_file_ref}
                                    type="file"
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    className="cursor-pointer"
                                    required={!editingCoop}
                                  />
                                  <p className="text-xs text-gray-500">PDF, JPG ou PNG {editingCoop ? '(opcional - já existe fatura)' : '(obrigatório para cada UC)'}</p>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  )}

                  {/* Botão para adicionar instalação */}
                  <div className="flex justify-center">
                    <Button
                      type="button"
                      onClick={addInstallation}
                      variant="outline"
                      size="lg"
                      className="text-blue-600 border-blue-600 hover:bg-blue-50"
                    >
                      + Adicionar Instalação
                    </Button>
                  </div>

                  {/* Observações e Controle */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-lg">Observações e Controle</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2 col-span-2">
                        <Label>Observações da Instalação</Label>
                        <Textarea value={formData.installation_notes} onChange={(e) => setFormData(prev => ({ ...prev, installation_notes: e.target.value }))} rows={3} />
                      </div>
                      <div className="space-y-2">
                        <Label>Situação *</Label>
                        <Select value={formData.situation} onValueChange={(value) => setFormData(prev => ({ ...prev, situation: value }))} disabled>
                          <SelectTrigger className="bg-gray-100">
                            <SelectValue placeholder="Pendente" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pendente</SelectItem>
                            <SelectItem value="sent_for_signature">Enviado para Assinatura</SelectItem>
                            <SelectItem value="active">Ativo</SelectItem>
                            <SelectItem value="suspended">Suspenso</SelectItem>
                            <SelectItem value="missing_login">Falta de Login</SelectItem>
                            <SelectItem value="excluded">Excluído</SelectItem>
                            <SelectItem value="modality_change">Troca de Modalidade</SelectItem>
                            <SelectItem value="awaiting_ownership_transfer">Aguard. Troca Tit.</SelectItem>
                            <SelectItem value="ownership_transfer_requested">Troca Solicitada</SelectItem>
                            <SelectItem value="accumulated_credits">Cred. Acum.</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-gray-500">Será definido como "Pendente". Apenas admin pode alterar.</p>
                      </div>
                    </div>
                  </div>

                  {/* Documentos Obrigatórios */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-lg">Documentos Obrigatórios</h3>
                    
                    <div className="bg-blue-50 p-4 rounded-lg space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="new-rg-cpf-file">
                            Cópia de RG/CPF {!editingCoop && <span className="text-red-500">*</span>}
                          </Label>
                          {editingCoop && documents.rg_cpf_file?.existing && (
                            <p className="text-xs text-green-600 mb-1">
                              ✓ Documento já enviado anteriormente. Faça novo upload apenas se desejar substituir.
                            </p>
                          )}
                          <Input
                            id="new-rg-cpf-file"
                            ref={newRgCpfFileRef}
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            data-testid="new-rg-cpf-upload"
                            className="cursor-pointer"
                            required={!editingCoop}
                          />
                          <p className="text-xs text-gray-500">PDF, JPG ou PNG {editingCoop ? '(opcional - já existe documento)' : '(obrigatório)'}</p>
                        </div>
                        
                        {formData.registration_type === "PJ" && (
                          <div className="space-y-2">
                            <Label htmlFor="new-cnpj-card-file">
                              Cópia de Cartão CNPJ {!editingCoop && <span className="text-red-500">*</span>}
                            </Label>
                            {editingCoop && documents.cnpj_card_file?.existing && (
                              <p className="text-xs text-green-600 mb-1">
                                ✓ Documento já enviado anteriormente. Faça novo upload apenas se desejar substituir.
                              </p>
                            )}
                            <Input
                              id="new-cnpj-card-file"
                              ref={newCnpjCardFileRef}
                              type="file"
                              accept=".pdf,.jpg,.jpeg,.png"
                              data-testid="new-cnpj-card-upload"
                              className="cursor-pointer"
                              required={!editingCoop}
                            />
                            <p className="text-xs text-gray-500">PDF, JPG ou PNG {editingCoop ? '(opcional - já existe documento)' : '(obrigatório para PJ)'}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <Button data-testid="coop-submit-button" type="submit" className="w-full bg-gradient-to-r from-blue-500 to-cyan-600">
                    {editingCoop ? 'Salvar Edição' : 'Cadastrar Cooperado'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <Card className="border-0 shadow-lg">
          <CardHeader>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <CardTitle className="flex items-center gap-2">
                  <UserCheck className="w-5 h-5" />
                  Lista de Cooperados
                </CardTitle>
                <div className="w-72">
                  <Input
                    placeholder="Buscar por nome, instalação ou parceiro..."
                    value={searchTerm}
                    onChange={handleSearch}
                    className="w-full"
                  />
                </div>
              </div>
              
              {/* Filtros - apenas para Admin */}
              {user?.role === 'admin' && (
                <div className="flex gap-3 flex-wrap">
                  <div className="w-60">
                    <Select value={filterDistributor} onValueChange={(value) => {
                      setFilterDistributor(value);
                      setCurrentPage(1);
                    }}>
                      <SelectTrigger>
                        <SelectValue placeholder="Filtrar por Distribuidora" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__all__">Todas as Distribuidoras</SelectItem>
                        {uniqueDistributors.map(dist => (
                          <SelectItem key={dist} value={dist}>{dist}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="w-60">
                    <Select value={filterPowerPlant} onValueChange={(value) => {
                      setFilterPowerPlant(value);
                      setCurrentPage(1);
                    }}>
                      <SelectTrigger>
                        <SelectValue placeholder="Filtrar por Usina" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__all__">Todas as Usinas</SelectItem>
                        <SelectItem value="__empty__">Sem Usina (vazio)</SelectItem>
                        {powerPlants
                          .filter(plant => plant.generator_installation_number && plant.generator_installation_number.trim() !== '')
                          .map(plant => (
                            <SelectItem key={plant.id} value={plant.generator_installation_number}>
                              {plant.name} ({plant.generator_installation_number})
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="w-60">
                    <Select value={filterStatus} onValueChange={(value) => {
                      setFilterStatus(value);
                      setCurrentPage(1);
                    }}>
                      <SelectTrigger>
                        <SelectValue placeholder="Filtrar por Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__all__">Todos os Status</SelectItem>
                        <SelectItem value="pending">Pendente</SelectItem>
                        <SelectItem value="sent_for_signature">Enviado para Assinatura</SelectItem>
                        <SelectItem value="active">Ativo</SelectItem>
                        <SelectItem value="suspended">Suspenso</SelectItem>
                        <SelectItem value="missing_login">Falta de Login</SelectItem>
                        <SelectItem value="excluded">Excluído</SelectItem>
                        <SelectItem value="cancelled">Cancelado</SelectItem>
                        <SelectItem value="modality_change">Troca de Modalidade</SelectItem>
                        <SelectItem value="awaiting_ownership_transfer">Aguard. Troca Tit.</SelectItem>
                        <SelectItem value="ownership_transfer_requested">Troca Solicitada</SelectItem>
                        <SelectItem value="accumulated_credits">Cred. Acum.</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {user?.role === 'admin' && (
                    <div className="w-60">
                      <Select value={filterPartner} onValueChange={(value) => {
                        setFilterPartner(value);
                        setCurrentPage(1);
                      }}>
                        <SelectTrigger>
                          <SelectValue placeholder="Filtrar por Parceiro" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__all__">Todos os Parceiros</SelectItem>
                          {partners
                            .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' }))
                            .map(partner => (
                              <SelectItem key={partner.id} value={partner.id}>
                                {partner.name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {user?.role === 'admin' && (
                    <div className="w-60">
                      <Select
                        value={filterModality}
                        onValueChange={(value) => {
                          setFilterModality(value);
                          setCurrentPage(1);
                        }}
                      >
                        <SelectTrigger data-testid="filter-modality-select">
                          <SelectValue placeholder="Filtrar por Modalidade" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__all__">Todas as Modalidades</SelectItem>
                          <SelectItem value="Geração Compartilhada">Geração Compartilhada</SelectItem>
                          <SelectItem value="Autoconsumo Remoto">Autoconsumo Remoto</SelectItem>
                            <SelectItem value="Autoconsumo C">Autoconsumo C</SelectItem>
                          <SelectItem value="Autoconsumo Remoto - Usina">Autoconsumo Remoto - Usina</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  <div className="w-44">
                    <Select
                      value={filterGroup}
                      onValueChange={(value) => {
                        setFilterGroup(value);
                        setCurrentPage(1);
                      }}
                    >
                      <SelectTrigger data-testid="filter-group-select">
                        <SelectValue placeholder="Filtrar por Grupo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__all__">Todos os Grupos</SelectItem>
                        <SelectItem value="Grupo A">Grupo A</SelectItem>
                        <SelectItem value="Grupo B">Grupo B</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {(filterDistributor && filterDistributor !== "__all__" || filterPowerPlant && filterPowerPlant !== "__all__" || filterStatus && filterStatus !== "__all__" || filterPartner && filterPartner !== "__all__" || filterGroup && filterGroup !== "__all__") && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setFilterDistributor("");
                        setFilterPowerPlant("");
                        setFilterStatus("");
                        setFilterPartner("");
                        setFilterGroup("");
                        setCurrentPage(1);
                      }}
                      className="text-gray-600"
                    >
                      Limpar Filtros
                    </Button>
                  )}
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">Carregando...</div>
            ) : cooperatives.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {searchTerm ? "Nenhum cooperado encontrado." : "Nenhum cooperado cadastrado ainda."}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {user?.role === 'admin' && (
                        <TableHead className="w-12">
                          <input
                            type="checkbox"
                            onChange={handleSelectAll}
                            checked={(() => {
                              const pageCoopIds = cooperatives.map(c => c.id);
                              return pageCoopIds.length > 0 && pageCoopIds.every(id => selectedCoops.includes(id));
                            })()}
                            className="w-4 h-4 cursor-pointer"
                          />
                        </TableHead>
                      )}
                      <TableHead>Nome</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>CPF/CNPJ</TableHead>
                      <TableHead>Instalação</TableHead>
                      <TableHead>Distribuidora</TableHead>
                      <TableHead>Grupo</TableHead>
                      <TableHead>Parceiro</TableHead>
                      <TableHead>Data de Cadastro</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Documentos</TableHead>
                      {canEdit() && <TableHead>Ações</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(() => {
                      // Dados já vêm paginados/filtrados do servidor
                      return filteredCooperatives;
                    })().map((coop) => (
                    <TableRow key={coop.id} data-testid={`coop-row-${coop.id}`}>
                      {user?.role === 'admin' && (
                        <TableCell>
                          <input
                            type="checkbox"
                            checked={selectedCoops.includes(coop.id)}
                            onChange={() => handleSelectCoop(coop.id)}
                            className="w-4 h-4 cursor-pointer"
                          />
                        </TableCell>
                      )}
                      <TableCell className="font-medium">{coop.name}</TableCell>
                      <TableCell>{coop.email || '-'}</TableCell>
                      <TableCell>{coop.cpf_cnpj || coop.cpf || '-'}</TableCell>
                      <TableCell>{coop.installation_number || '-'}</TableCell>
                      <TableCell>{coop.distributor || '-'}</TableCell>
                      <TableCell>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded border ${coop.consumer_group === "Grupo A" ? "bg-indigo-50 text-indigo-800 border-indigo-200" : "bg-slate-50 text-slate-700 border-slate-200"}`} data-testid={`coop-group-${coop.id}`}>
                          {coop.consumer_group === "Grupo A" ? "Grupo A" : "Grupo B"}
                        </span>
                      </TableCell>
                      <TableCell>
                        {user?.role === 'admin' ? (
                          <Select
                            value={coop.partner_id || ''}
                            onValueChange={(value) => handlePartnerChange(coop.id, value)}
                          >
                            <SelectTrigger className="w-[200px] h-8 text-xs">
                              <SelectValue placeholder="Selecione parceiro" />
                            </SelectTrigger>
                            <SelectContent>
                              {partners.map(p => (
                                <SelectItem key={p.id} value={p.id}>
                                  {p.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className="text-sm">{getPartnerName(coop.partner_id)}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {coop.created_at ? new Date(coop.created_at).toLocaleString('pt-BR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : '-'}
                      </TableCell>
                      <TableCell>
                        {canEdit() ? (
                          <Select 
                            value={coop.situation || 'pending'} 
                            onValueChange={(value) => handleStatusChange(coop.id, value)}
                          >
                            <SelectTrigger className="w-[200px]">
                              <SelectValue>
                                {coop.situation === 'active' && (
                                  <span className="flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-green-500"></span>
                                    Ativo
                                  </span>
                                )}
                                {coop.situation === 'cancelled' && (
                                  <span className="flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-red-500"></span>
                                    Cancelado
                                  </span>
                                )}
                                {coop.situation === 'pending' && (
                                  <span className="flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                                    Pendente
                                  </span>
                                )}
                                {coop.situation === 'sent_for_signature' && (
                                  <span className="flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                                    Enviado para Assinatura
                                  </span>
                                )}
                                {coop.situation === 'suspended' && (
                                  <span className="flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                                    Suspenso
                                  </span>
                                )}
                                {coop.situation === 'missing_login' && (
                                  <span className="flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                                    Falta de Login
                                  </span>
                                )}
                                {coop.situation === 'excluded' && (
                                  <span className="flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-gray-500"></span>
                                    Excluído
                                  </span>
                                )}
                                {coop.situation === 'accumulated_credits' && (
                                  <span className="flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-teal-500"></span>
                                    Cred. Acum.
                                  </span>
                                )}
                                {coop.situation === 'ownership_transfer_requested' && (
                                  <span className="flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-fuchsia-500"></span>
                                    Troca Solicitada
                                  </span>
                                )}
                                {coop.situation === 'awaiting_ownership_transfer' && (
                                  <span className="flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-indigo-500"></span>
                                    Aguard. Troca Tit.
                                  </span>
                                )}
                                {coop.situation === 'modality_change' && (
                                  <span className="flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-cyan-500"></span>
                                    Troca de Modalidade
                                  </span>
                                )}
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">
                                <span className="flex items-center gap-2">
                                  <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                                  Pendente
                                </span>
                              </SelectItem>
                              <SelectItem value="sent_for_signature">
                                <span className="flex items-center gap-2">
                                  <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                                  Enviado para Assinatura
                                </span>
                              </SelectItem>
                              <SelectItem value="cancelled">
                                <span className="flex items-center gap-2">
                                  <span className="w-2 h-2 rounded-full bg-red-500"></span>
                                  Cancelado
                                </span>
                              </SelectItem>
                              <SelectItem value="active">
                                <span className="flex items-center gap-2">
                                  <span className="w-2 h-2 rounded-full bg-green-500"></span>
                                  Ativo
                                </span>
                              </SelectItem>
                              <SelectItem value="suspended">
                                <span className="flex items-center gap-2">
                                  <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                                  Suspenso
                                </span>
                              </SelectItem>
                              <SelectItem value="missing_login">
                                <span className="flex items-center gap-2">
                                  <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                                  Falta de Login
                                </span>
                              </SelectItem>
                              <SelectItem value="excluded">
                                <span className="flex items-center gap-2">
                                  <span className="w-2 h-2 rounded-full bg-gray-500"></span>
                                  Excluído
                                </span>
                              </SelectItem>
                              <SelectItem value="accumulated_credits" data-testid="status-option-accumulated-credits">
                                <span className="flex items-center gap-2">
                                  <span className="w-2 h-2 rounded-full bg-teal-500"></span>
                                  Cred. Acum.
                                </span>
                              </SelectItem>
                              <SelectItem value="ownership_transfer_requested" data-testid="status-option-ownership-transfer-requested">
                                <span className="flex items-center gap-2">
                                  <span className="w-2 h-2 rounded-full bg-fuchsia-500"></span>
                                  Troca Solicitada
                                </span>
                              </SelectItem>
                              <SelectItem value="awaiting_ownership_transfer" data-testid="status-option-awaiting-ownership-transfer">
                                <span className="flex items-center gap-2">
                                  <span className="w-2 h-2 rounded-full bg-indigo-500"></span>
                                  Aguard. Troca Tit.
                                </span>
                              </SelectItem>
                              <SelectItem value="modality_change">
                                <span className="flex items-center gap-2">
                                  <span className="w-2 h-2 rounded-full bg-cyan-500"></span>
                                  Troca de Modalidade
                                </span>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            coop.situation === 'active'
                              ? 'bg-green-100 text-green-700'
                              : coop.situation === 'cancelled'
                              ? 'bg-red-100 text-red-700'
                              : coop.situation === 'sent_for_signature'
                              ? 'bg-blue-100 text-blue-700'
                              : coop.situation === 'suspended'
                              ? 'bg-orange-100 text-orange-700'
                              : coop.situation === 'excluded'
                              ? 'bg-gray-100 text-gray-700'
                              : coop.situation === 'missing_login'
                              ? 'bg-purple-100 text-purple-700'
                              : coop.situation === 'modality_change'
                              ? 'bg-cyan-100 text-cyan-700'
                              : coop.situation === 'awaiting_ownership_transfer'
                              ? 'bg-indigo-100 text-indigo-700'
                              : coop.situation === 'ownership_transfer_requested'
                              ? 'bg-fuchsia-100 text-fuchsia-700'
                              : coop.situation === 'accumulated_credits'
                              ? 'bg-teal-100 text-teal-700'
                              : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {coop.situation === 'active'
                              ? '🟢 Ativo'
                              : coop.situation === 'cancelled'
                              ? '🔴 Cancelado'
                              : coop.situation === 'sent_for_signature'
                              ? '🔵 Enviado p/ Assinatura'
                              : coop.situation === 'suspended'
                              ? '🟠 Suspenso'
                              : coop.situation === 'excluded'
                              ? '⚫ Excluído'
                              : coop.situation === 'missing_login'
                              ? '🟣 Falta de Login'
                              : coop.situation === 'modality_change'
                              ? '🔄 Troca de Modalidade'
                              : coop.situation === 'awaiting_ownership_transfer'
                              ? '🟦 Aguard. Troca Tit.'
                              : coop.situation === 'ownership_transfer_requested'
                              ? '🟪 Troca Solicitada'
                              : coop.situation === 'accumulated_credits'
                              ? '🟩 Cred. Acum.'
                              : '🟡 Pendente'}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {canEdit() ? (
                          <div className="flex gap-1 flex-wrap">
                            {coop.rg_cpf_document && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDownloadDocument(coop.id, 'rg_cpf', `RG_CPF_${coop.name}.pdf`)}
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                title="Download RG/CPF"
                              >
                                <Download className="w-3 h-3 mr-1" />
                                <span className="text-xs">RG/CPF</span>
                              </Button>
                            )}
                            {coop.energy_bill_document && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDownloadDocument(coop.id, 'energy_bill', `Conta_Energia_${coop.name}.pdf`)}
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                title="Download Conta de Energia"
                              >
                                <Download className="w-3 h-3 mr-1" />
                                <span className="text-xs">Conta</span>
                              </Button>
                            )}
                            {coop.cnpj_card_document && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDownloadDocument(coop.id, 'cnpj_card', `CNPJ_${coop.name}.pdf`)}
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                title="Download Cartão CNPJ"
                              >
                                <Download className="w-3 h-3 mr-1" />
                                <span className="text-xs">CNPJ</span>
                              </Button>
                            )}
                            {!coop.rg_cpf_document && !coop.energy_bill_document && !coop.cnpj_card_document && (
                              <Button
                                data-testid={`upload-docs-admin-${coop.id}`}
                                variant="ghost"
                                size="sm"
                                onClick={() => openUploadDialog(coop.id)}
                                className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                                title="Fazer upload de documentos"
                              >
                                <Upload className="w-3 h-3 mr-1" />
                                <span className="text-xs">Upload</span>
                              </Button>
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center">
                            <Button
                              data-testid={`upload-docs-${coop.id}`}
                              variant="ghost"
                              size="sm"
                              onClick={() => openUploadDialog(coop.id)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              title="Upload documentos"
                            >
                              <Upload className="w-4 h-4" />
                            </Button>
                            {(coop.rg_cpf_document_url || coop.energy_bill_document_url) && (
                              <span className="ml-2 text-xs text-green-600">✓</span>
                            )}
                          </div>
                        )}
                      </TableCell>
                      {canEdit() && (
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              data-testid={`edit-coop-${coop.id}`}
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                console.log('📝 Editando cooperado:', coop);
                                setEditingCoop(coop);
                                const newFormData = {
                                  registration_type: coop.registration_type || 'PF',
                                  name: coop.name || '',
                                  email: coop.email || '',
                                  phone: coop.phone || '',
                                  cpf_cnpj: coop.cpf_cnpj || '',
                                  legal_representative_cpf: coop.legal_representative_cpf || '',
                                  legal_representative_name: coop.legal_representative_name || '',
                                  installation_number: coop.installation_number || '',
                                  installation_class: coop.installation_class || '',
                                  compensation_modality: coop.compensation_modality || 'Geração Compartilhada',
                                  consumer_group: coop.consumer_group || 'Grupo B',
                                  generator_installation_number: coop.generator_installation_number || '',
                                  agent_number: coop.agent_number || '',
                                  partner_id: coop.partner_id || '',
                                  partner_commission: coop.partner_commission || 0,
                                  signature_date: coop.signature_date || '',
                                  status_date: coop.status_date ? String(coop.status_date).slice(0, 10) : '',
                                  tariff_discount: coop.tariff_discount || 0,
                                  contracted_kwh: coop.contracted_kwh || 0,
                                  distributor: coop.distributor || '',
                                  distributor_login: coop.distributor_login || '',
                                  distributor_password: coop.distributor_password || '',
                                  // Manter a senha atual se existir (para não perder ao editar)
                                  current_distributor_password: coop.distributor_password || '',
                                  birth_date: coop.birth_date || '',
                                  street: coop.street || '',
                                  number: coop.number || '',
                                  complement: coop.complement || '',
                                  zip_code: coop.zip_code || '',
                                  state: coop.state || '',
                                  city: coop.city || '',
                                  neighborhood: coop.neighborhood || '',
                                  installation_notes: coop.installation_notes || '',
                                  origin: coop.origin || '',
                                  status: coop.status || 'pending',
                                  situation: coop.situation || coop.status || 'pending'
                                };
                                console.log('📋 FormData que será setado:', newFormData);
                                setFormData(newFormData);
                                
                                // Armazenar informação sobre documentos existentes
                                setDocuments({
                                  rg_cpf_file: coop.rg_cpf_document ? { existing: true } : null,
                                  cnpj_card_file: coop.cnpj_card_document ? { existing: true } : null
                                });
                                
                                // Preencher installations com os dados da instalação sendo editada
                                const installationData = {
                                  installation_number: coop.installation_number || '',
                                  installation_class: coop.installation_class || '',
                                  street: coop.street || '',
                                  number: coop.number || '',
                                  complement: coop.complement || '',
                                  zip_code: coop.zip_code || '',
                                  state: coop.state || '',
                                  city: coop.city || '',
                                  neighborhood: coop.neighborhood || '',
                                  installation_notes: coop.installation_notes || '',
                                  energy_bill_file_ref: React.createRef(),
                                  has_energy_bill: coop.energy_bill_document ? true : false
                                };
                                console.log('🏠 Installation Data:', installationData);
                                setInstallations([installationData]);
                                setDialogOpen(true);
                              }}
                              className="text-green-600 hover:text-green-700 hover:bg-green-50"
                              title="Editar"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              data-testid={`generate-termo-${coop.id}`}
                              variant="ghost"
                              size="sm"
                              onClick={() => handleGenerateTermoAdesao(coop)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              title="Gerar Termo de Adesão"
                            >
                              <FileText className="w-4 h-4" />
                            </Button>
                            <Button
                              data-testid={`delete-coop-${coop.id}`}
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(coop.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              title="Excluir"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              {/* Pagination Controls */}
              <div className="flex items-center justify-between px-6 py-4 border-t mt-4">
                {/* Items per page selector */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Itens por página:</span>
                  <Select value={itemsPerPage.toString()} onValueChange={(v) => { setItemsPerPage(Number(v)); setCurrentPage(1); }}>
                    <SelectTrigger className="w-20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="20">20</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                  <span className="text-sm text-gray-500">
                    Mostrando {totalCount ? (currentPage - 1) * itemsPerPage + 1 : 0} - {Math.min(currentPage * itemsPerPage, totalCount)} de {totalCount} cooperados
                  </span>
                </div>
                
                {/* Pagination buttons */}
                {totalPages > 1 && (
                  <div className="flex gap-2">
                    <Button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      variant="outline"
                      size="sm"
                    >
                      Anterior
                    </Button>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(page => {
                          return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1;
                        })
                        .map((page, index, array) => {
                          const prevPage = array[index - 1];
                          const showEllipsis = prevPage && page - prevPage > 1;
                          
                          return (
                            <div key={page} className="flex items-center">
                              {showEllipsis && <span className="px-2 text-gray-400">...</span>}
                              <Button
                                onClick={() => setCurrentPage(page)}
                                variant={currentPage === page ? "default" : "outline"}
                                size="sm"
                                className={currentPage === page ? "bg-blue-600" : ""}
                              >
                                {page}
                              </Button>
                            </div>
                          );
                        })}
                    </div>
                    <Button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage >= totalPages}
                      variant="outline"
                      size="sm"
                    >
                      Próximo
                    </Button>
                  </div>
                )}
              </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upload Documents Dialog */}
        <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Upload de Documentos</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="rg-cpf-file">Cópia de RG/CPF</Label>
                <Input
                  id="rg-cpf-file"
                  ref={rgCpfFileRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  data-testid="rg-cpf-upload"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="energy-bill-file">Cópia da Fatura de Energia</Label>
                <Input
                  id="energy-bill-file"
                  ref={energyBillFileRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  data-testid="energy-bill-upload"
                />
              </div>

              <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-700">
                <p className="font-semibold mb-1">📁 O que acontecerá:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Documentos salvos localmente no servidor</li>
                  <li>Admin pode fazer download quando necessário</li>
                </ul>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={() => setUploadDialogOpen(false)}
                  variant="outline"
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button
                  data-testid="confirm-upload-button"
                  onClick={handleUploadDocuments}
                  disabled={uploading}
                  className="flex-1 bg-gradient-to-r from-blue-500 to-cyan-600"
                >
                  {uploading ? "Enviando..." : "Enviar"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
